import React, { FC, useEffect, useRef, useState } from 'react';
import './Modal.css';
import { FaSpinner, IoArrowBack, IoBackspace, IoClose } from '@/Shared/Lib/UI/Icons';
import { DEFAULT_MODAL_ANIMATION_DURATION, ModalData, PageLoader } from '@/Shared';


interface ModalProps {
	/** Data object controlling the modal content and title. Presence of data determines visibility */
	data?: ModalData | null;

	/** Callback fired when the modal is requested to close (backdrop click, close button, etc.) */
	onClose: () => void;

	onBack?: () => void

	/** Optional additional CSS class for the modal wrapper */
	className?: string;

	/** Optional size of the modal (sm, lg, xl) */
	size?: 'sm' | 'lg' | 'xl';

	/** Centers the modal vertically if true */
	centered?: boolean;

	/** Controls backdrop behavior: true = clickable, 'static' = non-clickable */
	backdrop?: boolean | 'static';

	/** Allows closing the modal with the keyboard (e.g., ESC key) */
	keyboard?: boolean;

	/** Maximum height of the modal content */
	maxHeight?: string;

	/** Optional CSS class for the modal dialog element */
	dialogClassName?: string;
}




/**
 * Modal component
 *
 * Generic modal dialog for displaying content.
 * Visibility is controlled entirely by the presence of `data`.
 * Includes fade-in and fade-out animations for smooth transitions.
 * Clicking outside the modal or on the close button triggers `onClose`.
 * 
 * Author: Samano CASTRE  
 * Date: January 20, 2026
 */
const Modal: FC<ModalProps> = ({ data, onClose, onBack, className }) => {

	/**
	 * Internal state controlling whether the modal should be rendered.
	 * Allows fade-out animation before unmounting the component.
	 */
	const [shouldRender, setShouldRender] = useState(!!data);

	/** Ref to the modal DOM element for applying animations */
	const modalRef = useRef<HTMLDivElement | null>(null);

	/** Boolean flag for modal visibility */
	const isVisible = !!data;

	const [modalData, setModalData] = useState<ModalData | null>();

	const loading = {
		title: "Modal",
		content: <>Modal en cours de préparation...</>
	}


	/** Adds fade-in CSS class */
	const fadeIn = () => {
		modalRef?.current?.classList.remove('fade-out');
		modalRef?.current?.classList.add('fade-in');
	}


	/** Adds fade-out CSS class */
	const fadeOut = () => {
		modalRef?.current?.classList.remove('fade-in');
		modalRef?.current?.classList.add('fade-out');
	}


	/**
	 * Effect hook for handling mount/unmount animations.
	 * When `isVisible` changes, triggers fade-in/out and sets render state after animation.
	 */
	useEffect(() => {

		if (isVisible) {
			setModalData(loading);
			fadeIn();
		}
		else {
			fadeOut();
		}

		const timer = setTimeout(() => {
			setShouldRender(isVisible);
			setModalData(isVisible ? data : null);
		}, isVisible ? 0 : DEFAULT_MODAL_ANIMATION_DURATION);

		return () => clearTimeout(timer);

	}, [isVisible, data]);

	/** Do not render anything if `shouldRender` is false */
	if (!shouldRender) {
		return null;
	}

	return (
		<div
			ref={modalRef}
			className={`modal-backdrop fade-in`}
			onClick={e => { e.stopPropagation(); onClose() }}>
			<div className={`${className ?? ""} modal-content`} onClick={e => e.stopPropagation()}>
				{onBack && <button className="modal-back-btn" type="button" onClick={onBack} title='Cliquer pour retourner.'><IoArrowBack /></button>}
				<button className="modal-close-btn" type="button" onClick={onClose} title='Cliquer pour fermer.'><IoClose /></button>
				<div className="Modal__header">
					<h5>{modalData?.title ?? ""}</h5>
				</div>
				<div className="Modal__body sm-scroll">{modalData?.content ?? ""}</div>
			</div>
		</div>
	);
};

export default Modal;
